import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHome,
  faFilter,
  faDownload,
  faBars,
  faSignOutAlt,
  faLocationDot,
  faFileInvoice,
  faUserPlus,
  faCalendar,
  faUser,
  faRotateLeft,
  faQuestionCircle
} from '@fortawesome/free-solid-svg-icons';
import { APP_ROUTES } from '../config/api.config';
import Footer from '../components/Footer';
import { getInvoices, Invoice } from '../services/api';
import jsPDF from 'jspdf';
import Loader from '../components/Loader';
import Pagination from '../components/Pagination';
import noDataFound from '../assets/images/no-data-found.png';
import { useLogoutHandler } from '../utils/logoutHandler';
import LogoutConfirmDialog from '../components/LogoutConfirmDialog';
import logoMain from '../assets/images/logo-main.png';
import Sidebar from '../components/Sidebar/Sidebar';

const DashboardContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: #f5f6fa;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const MobileHeader = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    background: white;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 1002;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }
`;

const HamburgerButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: #333;
  font-size: 24px;
  cursor: pointer;
  padding: 8px;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const MainContent = styled.div<{ isCollapsed: boolean }>`
  flex: 1;
  margin-left: ${props => props.isCollapsed ? '70px' : '240px'};
  padding: 0;
  padding-bottom: 60px;
  padding-top: 76px;
  transition: margin-left 0.3s ease;
  width: calc(100% - ${props => props.isCollapsed ? '70px' : '240px'});

  @media (max-width: 768px) {
    margin-left: 0;
    width: 100%;
    padding-top: 76px;
  }
`;

const TopBar = styled.div<{ isCollapsed: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background: white;
  border-radius: 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  margin-bottom: 24px;
  position: fixed;
  top: 0;
  right: 0;
  left: ${props => props.isCollapsed ? '70px' : '240px'};
  z-index: 100;
  transition: left 0.3s ease;

  @media (max-width: 768px) {
    left: 0;
    width: 100%;
  }
`;

const ToggleButton = styled.button`
  background: none;
  border: none;
  color: #333;
  font-size: 20px;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: background 0.3s ease;
  border: 1px solid #e0e0e0;

  &:hover {
    background: rgba(255, 107, 107, 0.1);
  }
`;

const UserProfile = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  position: relative;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 107, 107, 0.1);
  }
  
  @media (max-width: 768px) {
    justify-content: flex-end;
    padding: 0;
    gap: 8px;
  }
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    display: flex;
  }
`;

const UserName = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: #333;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 15px;
  }
`;

const UserAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #ff6b6b;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 18px;
  margin-left: 8px;

  @media (max-width: 768px) {
    margin-left: 0;
  }
`;

const UserDropdown = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  min-width: 200px;
  display: ${props => props.isOpen ? 'block' : 'none'};
  z-index: 1000;
  overflow: hidden;
`;

const DropdownItem = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: none;
  color: #666;
  font-size: 14px;
  cursor: pointer;
  text-align: left;

  &:hover {
    background: #f8f9fa;
  }

  svg {
    width: 16px;
    margin-right: 12px;
  }
`;

// Existing InvoiceList styled components
const PageContainer = styled.div`
  padding: 0;
  width: 100%;
  overflow-x: auto;
  margin: 0;
  padding-top: 15px;

  @media (max-width: 992px) {
    padding-top: 0;
  }

  @media (max-width: 768px) {
    padding: 0;
    margin: 0;
  }
`;

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  padding-top: 15px;
  
  @media (max-width: 992px) {
    padding-top: 0;
    gap: 15px;
  }
  
  @media (max-width: 768px) {
    gap: 10px;
    padding-top: 0;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  width: 100%;
  margin-bottom: 20px;
  padding: 0 20px;

  @media (max-width: 1180px) {
    margin-bottom: 15px;
    padding: 0 15px;
  }

  @media (max-width: 820px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
`;

const Title = styled.h1`
  font-size: 24px;
  color: #2d3748;
  margin: 0;
  flex: 1;

  @media (max-width: 1180px) {
    font-size: 22px;
  }

  @media (max-width: 768px) {
    font-size: 20px;
    width: 100%;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  margin: 0;
  flex: 0 0 auto;

  @media (max-width: 820px) {
    width: 100%;
    margin-top: 10px;
  }
  
  @media (max-width: 480px) {
    flex-direction: column;
    gap: 8px;
  }
`;

const ResetButton = styled.button`
  background-color: #6c757d;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 5px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 100px;
  height: 40px;

  &:hover {
    background-color: #5a6268;
  }
  
  @media (max-width: 480px) {
    width: 100%;
  }
`;

const FilterButton = styled.button`
  background-color: #ff6b6b;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 5px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 100px;
  height: 40px;

  &:hover {
    background-color: #ff8e53;
  }
  
  @media (max-width: 480px) {
    width: 100%;
  }
`;

// Add this new style for the entries dropdown
const EntriesSelector = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 20px;
  padding: 0 20px;
  font-size: 15px;
  
  select {
    padding: 5px 8px;
    border-radius: 4px;
    border: 1px solid #ddd;
    margin: 0 5px;
    min-width: 60px;
  }
  
  @media (max-width: 1180px) {
    padding: 0 15px;
    margin-bottom: 15px;
  }
  
  @media (max-width: 768px) {
    padding: 0 15px;
    margin-bottom: 15px;
    font-size: 14px;
  }
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  padding: 0 20px;
  width: 100%;
  -webkit-overflow-scrolling: touch;
  margin-bottom: 15px;
  
  @media (max-width: 992px) {
    padding: 0 15px;
    margin-bottom: 10px;
  }
  
  @media (max-width: 768px) {
    padding: 0 10px;
    margin-bottom: 5px;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;
  min-width: 600px;
  margin-top: 10px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  border: 1px solid #e9ecef;
  border-radius: 8px;
  overflow: hidden;

  th, td {
    padding: 12px 10px;
    text-align: left;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  /* Column widths */
  th:nth-child(1), td:nth-child(1) {
    width: 28%;
    padding-left: 16px;
  }
  
  th:nth-child(2), td:nth-child(2) {
    width: 30%;
  }
  
  th:nth-child(3), td:nth-child(3) {
    width: 14%;
  }
  
  th:nth-child(4), td:nth-child(4) {
    width: 10%;
    text-align: center;
  }
  
  th:nth-child(5), td:nth-child(5) {
    width: 18%;
    padding-right: 16px;
  }

  tbody tr:hover {
    background-color: #f8f9fa;
  }

  @media (max-width: 992px) {
    th, td {
      padding: 12px 8px;
    }
  }

  @media (max-width: 768px) {
    th, td {
      padding: 10px 6px;
      font-size: 14px;
    }
    
    /* Adjust column widths for mobile */
    th:nth-child(1), td:nth-child(1) {
      width: 28%;
      padding-left: 10px;
    }
    
    th:nth-child(2), td:nth-child(2) {
      width: 28%;
    }

    th:nth-child(5), td:nth-child(5) {
      padding-right: 10px;
    }
  }
`;

const Th = styled.th`
  text-align: left;
  padding: 12px 10px;
  background-color: #f8f9fa;
  border-bottom: 2px solid #e9ecef;
  color: #495057;
  font-weight: 600;
  position: relative;
  transition: background-color 0.2s;
  cursor: pointer;

  &:hover {
    background-color: #e9ecef;
  }

  @media (max-width: 992px) {
    padding: 12px 8px;
  }
  
  @media (max-width: 768px) {
    padding: 10px 6px;
    font-size: 14px;
  }
`;

const Td = styled.td`
  padding: 12px 10px;
  border-bottom: 1px solid #e9ecef;
  color: #495057;
  transition: background-color 0.2s;

  @media (max-width: 992px) {
    padding: 12px 8px;
  }
  
  @media (max-width: 768px) {
    padding: 10px 6px;
    font-size: 14px;
  }
`;

const DownloadButton = styled.button`
  background: none;
  border: none;
  color: #555;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
  margin: 0 auto;
  border-radius: 50%;

  &:hover {
    color: #ff6b6b;
    background-color: rgba(255, 107, 107, 0.1);
  }
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  padding: 12px;
  background: #f8d7da;
  border-radius: 4px;
  margin-bottom: 16px;
`;

// Add these new styled components for the filter modal
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 100px;
  z-index: 1000;
  
  @media (max-width: 768px) {
    padding-top: 60px;
    align-items: center;
  }
`;

const ModalContent = styled.div`
  background: white;
  padding: 24px;
  border-radius: 8px;
  width: 400px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
  
  @media (max-width: 768px) {
    width: 90%;
    max-width: 400px;
    padding: 20px;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-right: 20px;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  color: #333;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #666;
  padding: 4px;
  position: absolute;
  right: 16px;
  top: 16px;
  
  &:hover {
    color: #333;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
  width: 100%;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  color: #333;
  font-size: 14px;
`;

const DateInputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const DateInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  box-sizing: border-box;
  padding-right: 32px;
  
  &:focus {
    outline: none;
    border-color: #ff6b6b;
  }

  &:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
  }

  &::-webkit-calendar-picker-indicator {
    opacity: 0;
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    cursor: pointer;
  }
  
  @media (max-width: 768px) {
    padding: 10px;
    font-size: 16px;
  }
`;

const CalendarIcon = styled.span`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #666;
  pointer-events: none;
`;

const ButtonGroupFilter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
  width: 100%;

  @media (max-width: 768px) {
    gap: 8px;
    flex-direction: row;
  }
  
  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  border: none;
  
  ${props => props.variant === 'primary' ? `
    background: #ff6b6b;
    color: white;
    
    &:hover {
      background: #ff8e53;
    }
  ` : `
    background: #6c757d;
    color: white;
    
    &:hover {
      background: #5a6268;
    }
  `}
`;

// Add this with your other styled components
const LogoutMenuItem = styled.button<{ $isCollapsed: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: none;
  color: white;
  font-size: 15px;
  cursor: pointer;
  text-align: left;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  svg {
    width: 20px;
    margin-right: ${props => props.$isCollapsed ? '0' : '12px'};
  }

  span {
    display: ${props => props.$isCollapsed ? 'none' : 'block'};
  }
`;

const ErrorText = styled.span`
  color: #dc3545;
  font-size: 12px;
  margin-top: 4px;
  display: block;
`;

const formatDateForInput = (date: string) => {
  if (!date) return '';
  const [year, month, day] = date.split('-');
  return `${day}-${month}-${year}`;
};

const formatDateForAPI = (date: string) => {
  if (!date) return '';
  const [day, month, year] = date.split('-');
  return `${year}-${month}-${day}`;
};

const SearchInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const ClearButton = styled.button`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  color: #aaa;
  font-size: 16px;
  cursor: pointer;
  padding: 0 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  &:hover {
    color: #ff6b6b;
  }
`;

const SearchControl = styled.div`
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const SearchInput = styled.input`
  width: 200px;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #ff6b6b;
  }
`;

const EmptyStateImage = styled.img`
  width: 70%;
  height: auto;
  margin: 40px auto 24px auto;
  display: block;
`;

const ErrorImageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  margin: 24px 0;
`;

const PaginationContainer = styled.div`
  padding: 0 20px;
  margin-top: 20px;
  
  @media (max-width: 992px) {
    padding: 0 15px;
    margin-top: 15px;
  }
  
  @media (max-width: 768px) {
    padding: 0 10px;
    margin-top: 10px;
    display: flex;
    justify-content: center;
  }
`;

const MobileLogo = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;

const InvoiceList: React.FC = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [dateError, setDateError] = useState<string>('');
  const [sortColumn, setSortColumn] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const {
    showLogoutConfirm,
    handleLogoutClick,
    handleLogoutConfirm,
    handleLogoutCancel
  } = useLogoutHandler();

  // Get user data from localStorage that was saved during login
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userName = user.name || '';

  const handleLogout = () => {
    handleLogoutClick();
  };

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newStartDate = e.target.value;
    const formattedDate = formatDateForInput(newStartDate);
    setStartDate(formattedDate);
    setDateError('');
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEndDate = e.target.value;
    const formattedDate = formatDateForInput(newEndDate);

    if (startDate) {
      const startTimestamp = new Date(formatDateForAPI(startDate)).getTime();
      const endTimestamp = new Date(newEndDate).getTime();
      if (endTimestamp < startTimestamp) {
        setDateError('End date cannot be before start date');
        return;
      }
    }

    setEndDate(formattedDate);
    setDateError('');
  };

  const handleFilterApply = async () => {
    if (startDate && endDate) {
      const startTimestamp = new Date(formatDateForAPI(startDate)).getTime();
      const endTimestamp = new Date(formatDateForAPI(endDate)).getTime();
      if (endTimestamp < startTimestamp) {
        setDateError('End date cannot be before start date');
        return;
      }
    }

    try {
      setError('');
      setIsLoading(true);
      const { data, message } = await getInvoices(
        formatDateForAPI(startDate),
        formatDateForAPI(endDate)
      );
      setInvoices(data);
      setShowFilter(false);
      setDateError('');
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : "Failed to fetch filtered invoices";
      setError(errorMsg);
      setInvoices([]);
      setShowFilter(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetFilter = async () => {
    try {
      setError('');
      setIsLoading(true);
      const { data } = await getInvoices();
      setInvoices(data);
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : "Failed to reset and fetch invoices";
      setError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const downloadPDF = (invoice: Invoice) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const lineHeight = 10;
    let yPos = 20;

    // Helper function to ensure currency symbol displays correctly
    const formatCurrency = (amount: number) => {
      // Format the amount
      const formattedAmount = (amount / 100).toFixed(2);

      // Use 'Rs.' for Indian Rupee symbol to avoid font issues
      if (invoice.currency_symbol === '₹') {
        return `Rs. ${formattedAmount}`;
      }

      return `${invoice.currency_symbol}${formattedAmount}`;
    };

    // Add company logo/header
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 107, 107); // Brand color
    doc.text('WhatzPro', 20, yPos);

    // Invoice title
    doc.setFontSize(16);
    doc.setTextColor(0, 0, 0);
    doc.text('INVOICE', pageWidth - 20, yPos, { align: 'right' });
    yPos += 15;

    // Add a line
    doc.setLineWidth(0.5);
    doc.line(20, yPos, pageWidth - 20, yPos);
    yPos += 15;

    // Invoice details section
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Invoice Information:', 20, yPos);
    yPos += 8;

    doc.setFont('helvetica', 'normal');
    doc.text(`Invoice ID: ${invoice.id}`, 20, yPos);
    yPos += lineHeight;

    if (invoice.invoice_number) {
      doc.text(`Invoice Number: ${invoice.invoice_number}`, 20, yPos);
      yPos += lineHeight;
    }

    doc.text(`Status: ${invoice.status.toUpperCase()}`, 20, yPos);
    yPos += lineHeight;

    const issueDate = new Date(invoice.issued_at * 1000).toLocaleDateString();
    doc.text(`Issue Date: ${issueDate}`, 20, yPos);
    yPos += lineHeight;

    if (invoice.paid_at) {
      const paidDate = new Date(invoice.paid_at * 1000).toLocaleDateString();
      doc.text(`Paid Date: ${paidDate}`, 20, yPos);
      yPos += lineHeight;
    }

    // Billing period if available
    if (invoice.billing_start && invoice.billing_end) {
      const billingStart = new Date(invoice.billing_start * 1000).toLocaleDateString();
      const billingEnd = new Date(invoice.billing_end * 1000).toLocaleDateString();
      doc.text(`Billing Period: ${billingStart} to ${billingEnd}`, 20, yPos);
      yPos += lineHeight;
    }

    // Customer details section on the right side
    let customerYPos = 50;
    doc.setFont('helvetica', 'bold');
    doc.text('Customer Details:', pageWidth - 120, customerYPos);
    customerYPos += 8;

    doc.setFont('helvetica', 'normal');
    if (invoice.customer_details) {
      if (invoice.customer_details.customer_name) {
        doc.text(`Name: ${invoice.customer_details.customer_name}`, pageWidth - 120, customerYPos);
        customerYPos += lineHeight;
      }

      if (invoice.customer_details.customer_email) {
        doc.text(`Email: ${invoice.customer_details.customer_email}`, pageWidth - 120, customerYPos);
        customerYPos += lineHeight;
      }

      if (invoice.customer_details.customer_contact) {
        doc.text(`Phone: ${invoice.customer_details.customer_contact}`, pageWidth - 120, customerYPos);
        customerYPos += lineHeight;
      }
    }

    // Take the highest Y position from either section
    yPos = Math.max(yPos, customerYPos) + 10;

    // Line items table
    doc.setFont('helvetica', 'bold');
    doc.text('Item Details:', 20, yPos);
    yPos += 15;

    // Table headers
    const tableHeaders = ['Description', 'Quantity', 'Unit Price', 'Amount'];
    const columnWidths = [80, 30, 30, 30];
    let xPos = 20;

    // Draw table header
    doc.setFillColor(240, 240, 240);
    doc.rect(xPos, yPos - 10, pageWidth - 40, 10, 'F');

    doc.setFont('helvetica', 'bold');
    tableHeaders.forEach((header, i) => {
      doc.text(header, xPos, yPos - 3);
      xPos += columnWidths[i];
    });
    yPos += 5;

    // Draw line items
    if (invoice.line_items && invoice.line_items.length > 0) {
      invoice.line_items.forEach(item => {
        let itemXPos = 20;

        // Description
        doc.setFont('helvetica', 'normal');
        const description = item.name || 'N/A';
        const descriptionLines = doc.splitTextToSize(description, 80);
        doc.text(descriptionLines, itemXPos, yPos);

        // Quantity
        itemXPos += columnWidths[0];
        doc.text(item.quantity?.toString() || '1', itemXPos, yPos);

        // Unit Price
        itemXPos += columnWidths[1];
        doc.text(formatCurrency(item.unit_amount), itemXPos, yPos);

        // Amount
        itemXPos += columnWidths[2];
        doc.text(formatCurrency(item.amount), itemXPos, yPos);

        yPos += 10 + (descriptionLines.length - 1) * 5;
      });
    } else {
      doc.setFont('helvetica', 'normal');
      doc.text('No line items available', 20, yPos);
      yPos += 10;
    }

    // Draw a line after items
    yPos += 5;
    doc.setLineWidth(0.5);
    doc.line(20, yPos, pageWidth - 20, yPos);
    yPos += 15;

    // Payment summary
    doc.setFont('helvetica', 'bold');
    doc.text('Payment Summary:', pageWidth - 95, yPos);
    yPos += lineHeight;

    // Summary details
    doc.setFont('helvetica', 'normal');
    doc.text('Subtotal:', pageWidth - 95, yPos);
    doc.text(formatCurrency(invoice.gross_amount), pageWidth - 20, yPos, { align: 'right' });
    yPos += lineHeight;

    doc.text('Tax:', pageWidth - 95, yPos);
    doc.text(formatCurrency(invoice.tax_amount), pageWidth - 20, yPos, { align: 'right' });
    yPos += lineHeight;

    doc.setFont('helvetica', 'bold');
    doc.text('Total Amount:', pageWidth - 95, yPos);
    doc.text(formatCurrency(invoice.amount), pageWidth - 20, yPos, { align: 'right' });
    yPos += lineHeight;

    doc.text('Amount Paid:', pageWidth - 95, yPos);
    doc.text(formatCurrency(invoice.amount_paid), pageWidth - 20, yPos, { align: 'right' });
    yPos += lineHeight;

    doc.text('Balance Due:', pageWidth - 95, yPos);
    doc.text(formatCurrency(invoice.amount_due), pageWidth - 20, yPos, { align: 'right' });

    // Footer
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text('Thank you for your business!', 105, 280, { align: 'center' });
    doc.text('Generated by WhatzPro', 105, 285, { align: 'center' });

    // Save the PDF
    doc.save(`Invoice_${invoice.id.replace('inv_', '')}.pdf`);
  };

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Sort currentInvoices based on sort state
  const sortedInvoices = [...invoices].sort((a, b) => {
    if (!sortColumn) return 0;
    let aValue: any = a[sortColumn as keyof typeof a];
    let bValue: any = b[sortColumn as keyof typeof b];
    // Special handling for nested or custom fields
    if (sortColumn === 'description') {
      aValue = a.line_items && a.line_items.length > 0 ? a.line_items[0].name : '';
      bValue = b.line_items && b.line_items.length > 0 ? b.line_items[0].name : '';
    }
    if (sortColumn === 'date') {
      aValue = a.date;
      bValue = b.date;
    }
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }
    if (aValue === bValue) return 0;
    if (sortDirection === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        setIsLoading(true);
        const { data, message } = await getInvoices();
        setInvoices(data);
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : "Failed to fetch invoices";
        setError(errorMsg);
        setInvoices([]); // Clear previous data
      } finally {
        setIsLoading(false);
      }
    };

    fetchInvoices();
  }, []);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentInvoices = sortedInvoices.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  const handleItemsPerPageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setItemsPerPage(Number(event.target.value));
    setCurrentPage(1);
  };

  // Helper for sort arrow
  const getSortArrow = (column: string) => {
    if (sortColumn === column) {
      return sortDirection === 'asc' ? '▲' : '▼';
    }
    return '△';
  };

  // Add click outside handler
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsProfileOpen(false);
      }
    }

    // Attach the event listener
    document.addEventListener('mousedown', handleClickOutside);

    // Clean up the event listener
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownRef]);

  const toggleMobileMenu = () => {
    setIsMobileOpen(!isMobileOpen);
  };

  return (
    <DashboardContainer>
      <Sidebar
        isCollapsed={isCollapsed}
        isMobileOpen={isMobileOpen}
        onMobileClose={toggleMobileMenu}
      />

      <MainContent isCollapsed={isCollapsed}>
        <TopBar isCollapsed={isCollapsed}>
          <ToggleButton onClick={() => {
            if (window.innerWidth <= 768) {
              toggleMobileMenu();
            } else {
              setIsCollapsed(!isCollapsed);
            }
          }}>
            <FontAwesomeIcon icon={faBars} />
          </ToggleButton>
          <UserProfile ref={dropdownRef} onClick={() => setIsProfileOpen(!isProfileOpen)}>
            <UserInfo>
              <UserName>{userName}</UserName>
            </UserInfo>
            <UserAvatar>{userName.charAt(0)}</UserAvatar>
            <UserDropdown isOpen={isProfileOpen}>
              <DropdownItem onClick={() => navigate('/profile')}>
                <FontAwesomeIcon icon={faUser} />
                Profile
              </DropdownItem>
              <DropdownItem onClick={handleLogout}>
                <FontAwesomeIcon icon={faSignOutAlt} />
                Logout
              </DropdownItem>
            </UserDropdown>
          </UserProfile>
        </TopBar>

        <PageContainer>
          <ListContainer>
            <Header>
              <Title>Invoices List</Title>
              <ButtonGroup>
                <ResetButton onClick={handleResetFilter}>
                  <FontAwesomeIcon icon={faRotateLeft} />
                  Reset
                </ResetButton>
                <FilterButton onClick={() => setShowFilter(true)}>
                  <FontAwesomeIcon icon={faFilter} />
                  Filter
                </FilterButton>
              </ButtonGroup>
            </Header>

            {isLoading ? (
              <Loader />
            ) : error ? (
              <ErrorImageWrapper>
                <EmptyStateImage src={noDataFound} alt="Error loading data" />
              </ErrorImageWrapper>
            ) : currentInvoices.length === 0 ? (
              <ErrorImageWrapper>
                <EmptyStateImage src={noDataFound} alt="No invoices found" />
              </ErrorImageWrapper>
            ) : (
              <>
                <EntriesSelector>
                  Show
                  <select value={itemsPerPage} onChange={handleItemsPerPageChange}>
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={15}>15</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  entries
                </EntriesSelector>
                <TableWrapper>
                  <Table>
                    <thead>
                      <tr>
                        <Th onClick={() => handleSort('subscription_id')} style={{ cursor: 'pointer' }}>
                          Subscription ID <span style={{ color: sortColumn === 'subscription_id' ? '#333' : '#bbb' }}>{getSortArrow('subscription_id')}</span>
                        </Th>
                        <Th>Description</Th>
                        <Th onClick={() => handleSort('status')} style={{ cursor: 'pointer' }}>
                          Status <span style={{ color: sortColumn === 'status' ? '#333' : '#bbb' }}>{getSortArrow('status')}</span>
                        </Th>
                        <Th>Download</Th>
                        <Th onClick={() => handleSort('date')} style={{ cursor: 'pointer' }}>
                          Date <span style={{ color: sortColumn === 'date' ? '#333' : '#bbb' }}>{getSortArrow('date')}</span>
                        </Th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentInvoices.length > 0 ? (
                        currentInvoices.map((invoice) => (
                          <tr key={invoice.id}>
                            <Td>{invoice.subscription_id || 'N/A'}</Td>
                            <Td>{invoice.line_items && invoice.line_items.length > 0 ? invoice.line_items[0].name || 'N/A' : 'N/A'}</Td>
                            <Td>{invoice.status}</Td>
                            <Td>
                              <DownloadButton onClick={() => downloadPDF(invoice)}>
                                <FontAwesomeIcon icon={faDownload} />
                              </DownloadButton>
                            </Td>
                            <Td>{new Date(invoice.date * 1000).toLocaleDateString()}</Td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <Td colSpan={5}>No invoices found</Td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </TableWrapper>
                <PaginationContainer>
                  <Pagination
                    itemsPerPage={itemsPerPage}
                    totalItems={invoices.length}
                    paginate={paginate}
                    currentPage={currentPage}
                  />
                </PaginationContainer>
              </>
            )}
          </ListContainer>
        </PageContainer>

        {showFilter && (
          <ModalOverlay>
            <ModalContent>
              <ModalHeader>
                <ModalTitle>Filter Invoices by Date</ModalTitle>
                <CloseButton onClick={() => {
                  setShowFilter(false);
                  setStartDate("");
                  setEndDate("");
                  setDateError("");
                }}>×</CloseButton>
              </ModalHeader>

              <FormGroup>
                <Label>Start Date</Label>
                <DateInputWrapper>
                  <DateInput
                    type="date"
                    value={startDate ? formatDateForAPI(startDate) : ''}
                    required
                    onChange={handleStartDateChange}
                    max={new Date().toISOString().split('T')[0]}
                    placeholder="DD-MM-YYYY"
                  />
                  <CalendarIcon>
                    <FontAwesomeIcon icon={faCalendar} />
                  </CalendarIcon>
                </DateInputWrapper>
              </FormGroup>

              <FormGroup>
                <Label>End Date</Label>
                <DateInputWrapper>
                  <DateInput
                    type="date"
                    value={endDate ? formatDateForAPI(endDate) : ''}
                    required
                    onChange={handleEndDateChange}
                    min={startDate ? formatDateForAPI(startDate) : undefined}
                    max={new Date().toISOString().split('T')[0]}
                    placeholder="DD-MM-YYYY"
                  />
                  <CalendarIcon>
                    <FontAwesomeIcon icon={faCalendar} />
                  </CalendarIcon>
                </DateInputWrapper>
                {dateError && <ErrorText>{dateError}</ErrorText>}
              </FormGroup>

              <ButtonGroupFilter>
                <Button variant="secondary" onClick={() => {
                  setShowFilter(false);
                  setStartDate("");
                  setEndDate("");
                  setDateError("");
                }}>
                  Close
                </Button>
                <Button
                  variant="primary"
                  onClick={handleFilterApply}
                  disabled={!startDate || !endDate || !!dateError}
                >
                  Apply Filter
                </Button>
              </ButtonGroupFilter>
            </ModalContent>
          </ModalOverlay>
        )}
      </MainContent>
      <Footer isCollapsed={isCollapsed} />

      <LogoutConfirmDialog
        isOpen={showLogoutConfirm}
        onConfirm={handleLogoutConfirm}
        onCancel={handleLogoutCancel}
      />
    </DashboardContainer>
  );
};

export default InvoiceList;